/**
 * A panel that hangs off a button: where it is drawn, and what puts it away.
 *
 * The list window's filter bar raises two of these — the tags and the *View* knobs — and
 * neither of them is a list of rows, so neither can be the toolkit's `openPaneMenu`. That
 * function owns anchoring and dismissal but takes a `List<PaneMenuItem>`; a panel holding a
 * search field, a cloud of chips and a footer is not a list of items and never will be. The
 * toolkit says as much in its own stylesheet: a host's own value pickers *"mount
 * `.dt-hover-menu` directly rather than restyling a copy of it"*, and what a host then owes is
 * exactly the two things this file is — the placement and the dismissal.
 *
 * ── Why `fixed` rather than absolutely positioned in the header ──────────────
 *
 * The same reason [TagMenuPopover] gives, which is worth repeating because it is the mistake
 * anybody would make first: an absolutely-positioned panel is clipped by the nearest scrolling
 * or hidden ancestor, and this one has several — `.lp-list` is `overflow: hidden`, the rows
 * under it scroll, and the toolkit's pane body is hidden outside both. A panel drawn inside
 * that is a panel with two rows visible.
 *
 * `fixed` escapes all of it as long as nothing between here and the document root establishes
 * a containing block — no `transform`, `filter` or `contain`. If one ever appears, this file
 * and `TagMenuPopover` break together and visibly.
 *
 * ── The panel is a member, not a thing made when it opens ───────────────────
 *
 * Built once and shown, never created on open. That is not an optimisation: the tag panel
 * holds a `Find a tag…` field whose caret has to survive the repaint that typing into it
 * causes, which is LPN-41 in full. A panel that is constructed when it opens is a panel whose
 * field is a different node every keystroke.
 *
 * @see BookmarkListView
 * @see TagMenuPopover
 */
package se.soderbjorn.lunapin.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.KeyboardEvent
import kotlin.math.max
import kotlin.math.min

/**
 * One panel, hung under [anchor].
 *
 * @param anchor the button that raises it. Read for its rectangle on every placement and
 *   never written to — whether it looks pressed is the host's business, via the toolkit's
 *   `setMenuTriggerOpen`.
 * @param className the panel's own classes, on top of the toolkit's `dt-hover-menu` surface.
 * @param maxWidthPx how wide it may get. The panel is otherwise as wide as it needs to be,
 *   clamped to the viewport by [place].
 * @param onDismiss somebody clicked away, pressed `Escape`, or scrolled the page out from
 *   under it. **A request, not a notification**: the open/closed state lives in the view
 *   model like every other piece of this window's state, so this asks the host to change it
 *   and the host tells this panel afterwards. Closing here directly would be a second source
 *   of truth about whether the panel is up, and the two would disagree on the first repaint.
 */
internal class AnchoredPanel(
    private val anchor: HTMLElement,
    className: String,
    private val maxWidthPx: Double,
    private val onDismiss: () -> Unit,
) {
    /** What to mount. Lives wherever the host put it; `fixed` means the parent is moot. */
    val element: HTMLElement = element("div", "dt-hover-menu $className").apply {
        setAttribute("role", "dialog")
        hidden = true
    }

    val isOpen: Boolean get() = !element.hidden

    init {
        // ── Four listeners, none of them ever removed ────────────────────────
        //
        // The same trade `TagMenuPopover` and `observeWidth` make, and for the same reason:
        // every one of them is guarded on `isOpen`, a closed window's panel is never open,
        // and `WindowBodies.dispose` drops the whole body — at which point these closures are
        // the only thing referring to a detached node and the collector takes the lot. The
        // dismissal callbacks would reach a view model nobody is collecting, which publishes
        // a state into a flow with no subscribers and stops there.
        //
        // Capture phase, and on the document rather than on the panel: a press that lands
        // anywhere else has to reach this before whatever it was aimed at handles it and
        // repaints the window under us. Presses inside the panel and on the anchor are
        // exempt — the anchor's own click is what toggles the panel, and letting this fire
        // too would close and reopen it in one press.
        document.addEventListener(
            "mousedown",
            { event ->
                if (!isOpen) return@addEventListener
                val target = event.target as? org.w3c.dom.Node ?: return@addEventListener
                if (!element.contains(target) && !anchor.contains(target)) onDismiss()
            },
            true,
        )
        // Escape closes, and stops there: it must not also reach the toolkit, which would
        // take the window down with the panel.
        document.addEventListener(
            "keydown",
            { event ->
                val key = (event as KeyboardEvent).key
                if (isOpen && key == "Escape") {
                    event.preventDefault()
                    event.stopPropagation()
                    onDismiss()
                }
            },
            true,
        )
        // A `fixed` panel does not travel with the button it hangs off, so anything that
        // moves the button has to move it too — a pane resize, a split bar dragged, a
        // scroll somewhere above. Watched in the capture phase because `scroll` does not
        // bubble, and the panel's own scrolling is ignored or reading a long list of chips
        // would shuffle it under the cursor on every wheel tick.
        window.addEventListener("resize", { if (isOpen) place() })
        document.addEventListener(
            "scroll",
            { event -> if (isOpen && event.target != element) place() },
            true,
        )
    }

    /** Show it, and put it where the anchor is now. */
    fun open() {
        if (!element.hidden) {
            place()
            return
        }
        element.hidden = false
        place()
    }

    fun close() {
        element.hidden = true
    }

    /**
     * Put the panel under its button — right edges aligned, clamped into the viewport, and
     * never taller than the room below.
     *
     * Right-aligned because these buttons sit at the right end of a filter bar, and a panel
     * that grew rightwards from one would leave the screen. The clamp then handles the case
     * the alignment cannot: a panel wider than the space to the left of its button, which is
     * an ordinary thing in a narrow pane.
     *
     * The height cap is what keeps this honest in a short window. A panel is a control, not a
     * page: it scrolls inside itself — see `.lp-list-panel-grid` — rather than running off the
     * bottom of the screen with its footer somewhere below the taskbar.
     */
    private fun place() {
        val rect = anchor.getBoundingClientRect()
        val style = element.style
        // Cleared before measuring, or every placement would inherit the last one's width and
        // the panel would only ever get narrower.
        style.left = "0px"
        style.top = "0px"
        style.maxWidth = "${min(maxWidthPx, window.innerWidth - 2 * VIEWPORT_MARGIN_PX)}px"
        style.maxHeight = "${window.innerHeight - rect.bottom - GAP_PX - VIEWPORT_MARGIN_PX}px"
        val width = element.offsetWidth.toDouble()
        val left = min(
            max(rect.right - width, VIEWPORT_MARGIN_PX),
            window.innerWidth - width - VIEWPORT_MARGIN_PX,
        )
        style.left = "${max(left, VIEWPORT_MARGIN_PX)}px"
        style.top = "${rect.bottom + GAP_PX}px"
    }

    private companion object {
        /** Between the button and the panel it raised. */
        const val GAP_PX = 5.0

        /** How close to the edge of the window a panel may get. */
        const val VIEWPORT_MARGIN_PX = 8.0
    }
}
